import React, { useState, useEffect } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import dayjs from 'dayjs'
import { StyledDropdownToggle } from '../../StandardDropdown/StandardDropdown.styles'
import { PlaceholderText } from '../Inputs.styles'
import {
  PrimaryButton,
  SecondaryButton,
  StandardButtonSize,
} from '../../Buttons'
import { FlexColumn, FlexRow, FlexAlign } from '../../Flex'
import { CalendarProxy } from './DatePicker.styles'

// The long-standing default, kept so existing callers render unchanged.
const DEFAULT_DISPLAY_FORMAT = 'YYYY-MM-DD hh:mm A'

export interface DatePickerProps {
  placeholder?: string
  onChange?: (date: Date) => void
  initialDate?: Date
  isDisabled?: boolean
  // Opt in to showing `placeholder` until a date is actually picked, instead of
  // displaying today. For genuinely optional dates, where defaulting the
  // display to today reads as "this is the chosen date".
  emptyUntilSet?: boolean
  // Overrides how the chosen date is rendered in the closed field, as a dayjs
  // format string (DATE_FORMATS members work). Defaults to date + time; pass a
  // date-only format where the time is noise, e.g. a due date. Mirrors
  // TrusteroDateRange's prop of the same name, which takes date-fns tokens --
  // hence a plain string rather than a shared type.
  dateDisplayFormat?: string
}

export const DatePicker = ({
  placeholder,
  onChange,
  initialDate,
  isDisabled,
  emptyUntilSet,
  dateDisplayFormat = DEFAULT_DISPLAY_FORMAT,
}: DatePickerProps): JSX.Element => {
  const [date, setDate] = useState<Date | undefined>(
    emptyUntilSet ? initialDate : new Date(),
  )
  const [stagedDate, setStagedDate] = useState(initialDate || new Date())
  const [showDropdown, setShowDropdown] = useState(false)

  useEffect(() => {
    const newDate = initialDate || (emptyUntilSet ? undefined : new Date())
    setDate(newDate)
    setStagedDate(newDate || new Date())
  }, [initialDate, emptyUntilSet])

  useEffect(() => {
    if (showDropdown) {
      setStagedDate(date || new Date())
    }
  }, [showDropdown, date])

  const handleCalendarChange = (newDate: Date) => {
    setStagedDate(newDate)
  }

  const handleSave = () => {
    setDate(stagedDate)
    onChange?.(stagedDate)
    setShowDropdown(false)
  }

  const handleCancel = () => {
    setStagedDate(date || new Date())
    setShowDropdown(false)
  }

  return (
    <Dropdown
      style={{ width: '100%' }}
      show={showDropdown}
      onToggle={(nextShow) => setShowDropdown(nextShow)}
    >
      <Dropdown.Toggle
        as={StyledDropdownToggle}
        style={{
          height: '40px',
          width: '100%',
        }}
        disabled={isDisabled}
      >
        {date ? (
          dayjs(date).format(dateDisplayFormat)
        ) : (
          <PlaceholderText>{placeholder || 'Select Date'}</PlaceholderText>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <FlexColumn isFullHeight isFullWidth>
          <CalendarProxy
            color="black"
            date={stagedDate}
            minDate={new Date()}
            onChange={handleCalendarChange}
          />
          <FlexRow
            justify={FlexAlign.FLEX_END}
            align={FlexAlign.CENTER}
            gap={8}
            isFullWidth
            $padding={8}
          >
            <SecondaryButton
              size={StandardButtonSize.SMALL}
              onClick={handleCancel}
              text="Cancel"
            />
            <PrimaryButton
              size={StandardButtonSize.SMALL}
              onClick={handleSave}
              text="Save"
            />
          </FlexRow>
        </FlexColumn>
      </Dropdown.Menu>
    </Dropdown>
  )
}
